import React from 'react';
import PropTypes from 'prop-types';
import { styleOverrideReason } from '@ids/core';
import Position, { RootRef } from '@ids/position';
import Transition, { transitions } from '@ids/transition';
import { themes, getTheme, ThemeContext } from '@ids/theme';
import ClickAwayListener from '@ids/click-away-listener';
import IDSContext from '@ids/context';
import ZIndex from '@ids/zindex';
import TooltipInner from './TooltipInner';
import {
  Wrapper,
  OuterContainer,
  Container,
  Title,
  CloseIconControlWrapper
} from './StyledTooltip';

const { bool, func, node, oneOfType, string, number, object } = PropTypes;

/** */
export default class Tooltip extends React.Component {
  static contextType = ThemeContext;
  static propTypes = {
    /**
     * Aria described by property to be used in case validation tooltips.
     */
    ariaDescribedById: string,
    /**
     * autoFocus: focus on Tooltip when it is opened
     * Default: false
     */
    // eslint-disable-next-line react/boolean-prop-naming
    autoFocus: bool,
    /**
     * Allows easier access to elements while running functional tests. Renders
     * as a data-automation-id attribute on the mounted Tooltip div. Defaults to tooltip.
     */
    automationId: string,
    /**
     * The children is a target element that is the reference for the tooltip to show.
     */
    children: oneOfType([string, node]).isRequired,
    /**
     * If true, a 'X' close icon will appear by Tooltip to allow user to
     * close (dismiss). Defaults to false.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    dismissible: bool,
    /**
     * Time in milliseconds before Tooltip will start to dismisss after
     * trigger event fires.
     * Default: 0
     */
    duration: number,
    /**
     * Handler that's fired when close icon ('X') is clicked. Tooltip will still
     * close by default if this is supplied.
     */
    onClose: func,
    /**
     * Handler that's fired when any keydown event fire on Tooltip content.
     */
    onKeyDown: func,
    /**
     * Handle that is fired when any keydown event fire on close icon control in tooltip.
     */
    onCloseButtonKeyDown: func,
    /**
     * Whether to show the tooltip
     * */
    // eslint-disable-next-line react/boolean-prop-naming
    open: bool,
    /**
     * Called when tooltip opened.
     */
    onShow: func,
    /**
     * Positions tooltip from right or left of target selector
     * */
    offsetX: number,
    /**
     * Positions tooltip from top or bottom of target selector
     * */
    offsetY: number,
    /**
     * Defines where tooltip should appear relative to its target elements.
     * If nothing specified, it will attempt to automatically position
     * the tooltip where space is available.
     * Options: top, bottom, left, right
     * Default: top
     */
    position: string,
    /**
     * The optional styles applied to the position node wrapped around the root node.
     */
    // eslint-disable-next-line react/forbid-prop-types
    stylePosition: object,
    /**
     * Defines where tooltip should align relative to its target element.
     * Options: top, bottom, left, right
     * NOTE: horizontal alignments cannot be paired with horizontal positions
     */
    alignment: string,
    /**
     * containerRef: callback that returns the ref for Tooltip container.
     */
    containerRef: func,
    /**
     * A Tooltip title.
     */
    title: string,
    /**
     * The inner content of the Tooltip.
     */
    message: oneOfType([string, node]),
    /**
     * role:  property to be used in case validation tooltips.
     */
    role: string,
    /**
     * Disable response to focus events.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disableFocusListener: bool,
    /**
     * Disable response to hover events.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disableHoverListener: bool,
    /**
     * Disable response to click events.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disableClickListener: bool,
    /**
     * Disable response to touch events.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disableTouchListener: bool,
    /**
     * enable onClose trigger on click away.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    enableClickAway: bool,
    /**
     * When there is an application level theme defined, use this prop to override
     * (e.g. 'intuit', mint', 'proconnect', 'quickbooks', 'turbotax')
     */
    theme: PropTypes.oneOf(themes),
    /**
     * preventOverflow: an object variable; used to prevent Tooltip from being positioned outside the boundary.
     *  More detail in @ids/position preventOverflow API document.
     * */
    // eslint-disable-next-line react/forbid-prop-types
    preventOverflow: object,
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason
  };

  static defaultProps = {
    ariaDescribedById: null,
    autoFocus: false,
    automationId: undefined,
    dismissible: false,
    duration: 0,
    offsetY: 0,
    offsetX: 0,
    onClose() {},
    onKeyDown() {},
    onCloseButtonKeyDown() {},
    onShow() {},
    disableFocusListener: false,
    disableHoverListener: false,
    disableClickListener: false,
    disableTouchListener: false,
    enableClickAway: false,
    open: null,
    position: 'top',
    alignment: 'left',
    containerRef() {},
    role: 'tooltip',
    title: '',
    message: '',
    stylePosition: null,
    theme: undefined,
    preventOverflow: undefined,
    styleOverrideReason: undefined
  };

  constructor(props) {
    super(props);
    this.state = {
      open: false
    };
    this.isControlled = props.open !== null;
    this.isClickOpened = false;
    this.timer = null;
    this.target = null;
    if (props.stylePosition) {
      console.warn(
        'stylePosition prop was added previously as a hack for zIndex issue. We now have permanant solution for issue. Please refrain from using this prop'
      );
    }
  }

  componentDidMount() {
    if (this.props.open) {
      this.forceUpdate();
    }
  }

  componentDidUpdate() {
    if (this.props.open || this.state.open) {
      this.setTimer();
      if (this.props.autoFocus === true) {
        setTimeout(() => {
          if (this.container !== null) {
            this.container.focus();
          }
        });
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer);
  }

  onRootRef = ref => {
    this.target = ref;
  };

  setTimer = () => {
    if (!this.props.dismissible && this.props.duration > 0) {
      this.timer = setTimeout(this.handleClose, this.props.duration);
    }
  };

  handleContainerRef = ref => {
    this.container = ref;
    this.props.containerRef(ref);
  };

  // handle keyboard event
  handleKeyDown = event => {
    event.stopPropagation();
    const { onKeyDown, dismissible } = this.props;

    if (dismissible && event.key === 'Escape') {
      this.handleClose(event);
    }

    onKeyDown(event);
  };

  handleOpen = event => {
    if (!this.isControlled && !this.state.open) {
      this.setState({ open: true });
    }

    this.props.onShow(event);
  };

  handleClose = event => {
    this.isClickOpened = false;
    if (!this.isControlled) {
      this.setState({ open: false });
    }

    this.props.onClose(event);
  };

  handleClick = event => {
    const childrenProps = this.props.children.props;
    if (childrenProps.onClick) {
      childrenProps.onClick(event);
    }

    /* eslint-disable-next-line no-negated-condition */
    if (!this.state.open) {
      this.isClickOpened = true;
      this.handleOpen(event);
    } else {
      this.handleClose(event);
    }
  };

  handleTouchStart = event => {
    const childrenProps = this.props.children.props;
    if (childrenProps.onTouchStart) {
      childrenProps.onTouchStart(event);
    }

    this.handleOpen(event);
  };

  handleTouchEnd = event => {
    const childrenProps = this.props.children.props;
    if (childrenProps.onTouchEnd) {
      childrenProps.onTouchEnd(event);
    }

    this.handleClose(event);
  };

  handleFocus = event => {
    if (this.target === document.activeElement) {
      this.handleMouseOver(event);
    }

    const childrenProps = this.props.children.props;
    if (childrenProps.onFocus) {
      childrenProps.onFocus(event);
    }
  };

  handleBlur = event => {
    const childrenProps = this.props.children.props;
    if (childrenProps.onBlur) {
      childrenProps.onBlur(event);
    }

    this.handleMouseLeave(event);
  };

  handleMouseOver = event => {
    const childrenProps = this.props.children.props;
    if (event.type === 'mouseover' && childrenProps.onMouseOver) {
      childrenProps.onMouseOver(event);
    }

    this.handleOpen(event);
  };

  handleMouseLeave = event => {
    const childrenProps = this.props.children.props;
    if (event.type === 'mouseleave' && childrenProps.onMouseLeave) {
      childrenProps.onMouseLeave(event);
    }

    this.handleClose(event);
  };

  handleClickAway = event => {
    if (this.isClickOpened && event.target !== this.target) {
      this.isClickOpened = false;
      this.handleClose(event);
    }
  };

  renderContent() {
    const {
      dismissible,
      enableClickAway,
      message,
      title,
      role,
      automationId,
      onCloseButtonKeyDown,
      ariaDescribedById,
      onShow,
      className, // eslint-disable-line
      style, // eslint-disable-line
      ...restProps
    } = this.props;

    const { currentTheme } = getTheme(this.props, this.context);

    const tooltip = (
      <OuterContainer {...restProps}>
        <Container
          ref={this.handleContainerRef}
          tabIndex="-1"
          role={role}
          onKeyDown={this.handleKeyDown}
        >
          {title && <Title theme={currentTheme}>{title}</Title>}
          <TooltipInner ariaDescribedById={ariaDescribedById}>
            {message}
          </TooltipInner>
        </Container>
        {dismissible && (
          <CloseIconControlWrapper
            inverse
            aria-label="Close Tooltip"
            theme={currentTheme}
            onClick={this.handleClose}
            onKeyDown={onCloseButtonKeyDown}
          />
        )}
      </OuterContainer>
    );

    const tooltipWithClickAway = (
      <ClickAwayListener onClickAway={this.handleClickAway}>
        {tooltip}
      </ClickAwayListener>
    );

    return (
      <Wrapper
        theme={currentTheme}
        data-automation-id={automationId}
        className={className}
        style={style}
      >
        {enableClickAway || !dismissible ? tooltipWithClickAway : tooltip}
      </Wrapper>
    );
  }

  render() {
    const {
      offsetX,
      offsetY,
      position,
      stylePosition,
      alignment,
      open,
      disableHoverListener,
      disableFocusListener,
      disableClickListener,
      disableTouchListener,
      preventOverflow,
      children
    } = this.props;

    const childrenProps = {};

    if (!disableClickListener) {
      childrenProps.onClick = this.handleClick;
    }

    if (!disableTouchListener) {
      childrenProps.onTouchStart = this.handleTouchStart;
      childrenProps.onTouchEnd = this.handleTouchEnd;
    }

    if (!disableHoverListener) {
      childrenProps.onMouseOver = this.handleMouseOver;
      childrenProps.onMouseLeave = this.handleMouseLeave;
    }

    if (!disableFocusListener) {
      childrenProps.onFocus = this.handleFocus;
      childrenProps.onBlur = this.handleBlur;
    }

    let childrenTemp = null;
    if (typeof children === 'string') {
      childrenTemp = <span>{children}</span>;
    } else {
      childrenTemp = children;
    }

    const childrenWithProps = React.cloneElement(childrenTemp, childrenProps);

    const content = this.renderContent();

    const openProp = this.isControlled ? open : this.state.open;

    let positionStyling;
    if (stylePosition) {
      positionStyling = { ...stylePosition, pointerEvents: 'none' };
    } else {
      positionStyling = { pointerEvents: 'none' };
    }

    const positionElement = (
      <Position
        offsetX={offsetX}
        offsetY={offsetY}
        targetElement={this.target}
        position={position}
        style={positionStyling}
        alignment={alignment}
        preventOverflow={preventOverflow}
        show={openProp}
      >
        <Transition
          unmountOnExit
          transition={openProp ? transitions.enter : transitions.exit}
        >
          {content}
        </Transition>
      </Position>
    );

    return (
      <>
        {children && (
          <RootRef rootRef={this.onRootRef}>{childrenWithProps}</RootRef>
        )}
        {/* eslint-disable-next-line no-negated-condition */}
        {!stylePosition ? (
          <IDSContext.Consumer>
            {context => (
              <ZIndex
                assignZIndex={openProp}
                getCurrentBaseZIndex={context.getCurrentBaseZIndex}
                incrementBaseZIndex={context.incrementBaseZIndex}
                decrementBaseZIndex={context.decrementBaseZIndex}
              >
                {positionElement}
              </ZIndex>
            )}
          </IDSContext.Consumer>
        ) : (
          positionElement
        )}
      </>
    );
  }
}

Tooltip.displayName = 'Tooltip';
